import type { ReactNode } from 'react';

/**
 * As marcas de texto do Markdown, no que o papel entende.
 *
 * O texto do trabalho é escrito em Markdown: quem cola do Word traz negrito e
 * itálico junto, e quem escreve aqui usa as mesmas marcas. Esta função é o mínimo
 * que o papel precisa — `**negrito**` e `*itálico*` viram `<strong>` e `<em>`, e
 * ela serve para **todo** texto do documento: os parágrafos, as citações, os campos
 * e também as referências (o motor marca o título da obra com `**`).
 *
 * Só estas duas marcas existem aqui. **Não** se reconhece `_itálico_` de propósito:
 * um `_` solto é comum em endereço de site e em nome de arquivo, e a lista de
 * referências é justamente onde ele aparece — um itálico inventado no meio de uma
 * URL seria um defeito silencioso no que a banca confere.
 *
 * **Sem `dangerouslySetInnerHTML`**: o texto é de quem escreve, mas HTML também
 * seria — e um `<script>` colado no meio do parágrafo não tem o que fazer dentro
 * da página do gerador. Aqui só existem duas marcas reconhecidas; todo o resto sai
 * como texto, exatamente como foi digitado.
 */
export function comMarcas(texto: string): ReactNode[] {
  /*
   * O `split` com grupo de captura devolve as marcas junto com o texto, então a
   * lista alterna texto e marca — e cada pedaço é reconhecido pela forma.
   */
  const pedacos = texto.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);

  return pedacos.filter(Boolean).map((pedaco, indice) => {
    if (pedaco.startsWith('**') && pedaco.endsWith('**') && pedaco.length > 4) {
      return <strong key={indice}>{pedaco.slice(2, -2)}</strong>;
    }
    if (pedaco.startsWith('*') && pedaco.endsWith('*') && pedaco.length > 2) {
      return <em key={indice}>{pedaco.slice(1, -1)}</em>;
    }
    return pedaco;
  });
}
